/* eslint-disable no-undef */
import React, { useState, useCallback } from "react";
import {
  Form,
  Input,
  Button,
  Icon,
  Grid,
  Image,
  Segment,
  Message,
} from "semantic-ui-react";
import { useDropzone } from "react-dropzone";
import { image } from "../../../../assets";
import { buildStorageUrl } from "../../../../utils";
import "./PersonalInfoTab.scss";

export function PersonalInfoTab({
  userData,
  loading,
  formMessage,
  formik,
  isEditing,
  setIsEditing,
}) {
  const [avatar, setAvatar] = useState(null);
  const [fileAvatar, setFileAvatar] = useState(null);

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    setAvatar(URL.createObjectURL(file));
    setFileAvatar(file);
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      "image/jpeg": [],
      "image/png": [],
    },
    onDrop,
  });

  const getAvatar = () => {
    if (fileAvatar) {
      return avatar;
    } else if (userData?.avatar) {
      return buildStorageUrl(userData.avatar);
    }
    return image.noAvatar;
  };

  const handleCancelEdit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsEditing(false);
    formik.resetForm();
    setAvatar(null);
    setFileAvatar(null);
  };

  const handleStartEdit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsEditing(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Si hay un archivo de avatar nuevo, agregarlo al formik antes de enviar
    if (fileAvatar) {
      formik.setFieldValue("fileAvatar", fileAvatar);
    }

    // Llamar al submit de formik
    formik.handleSubmit(e);

    // Limpiar el avatar después de enviar
    if (fileAvatar) {
      setAvatar(null);
      setFileAvatar(null);
    }
  };

  return (
    <Segment padded className="personal-info-tab">
      <Grid stackable>
        <Grid.Row>
          <Grid.Column mobile={16} tablet={5} computer={4}>
            <div className="personal-info-tab__avatar-container">
              <div
                {...getRootProps()}
                className="personal-info-tab__avatar-dropzone"
              >
                <input {...getInputProps()} />
                <Image
                  avatar
                  className="user-avatar"
                  centered
                  size="small"
                  src={getAvatar()}
                />
                <div className="personal-info-tab__avatar-overlay">
                  <Icon name="camera" size="big" />
                  <p>Cambiar foto</p>
                </div>
              </div>
              <div className="personal-info-tab__user-name">
                <h3>
                  {`${userData?.firstname || ""} ${userData?.lastname || ""}`}{" "}
                  {userData?.secondLastname || ""}
                </h3>
              </div>
            </div>
          </Grid.Column>

          <Grid.Column mobile={16} tablet={11} computer={12}>
            <Form
              className="personal-info-tab__form"
              onSubmit={isEditing ? handleSubmit : (e) => e.preventDefault()}
            >
              {formMessage.show && (
                <Message
                  success={formMessage.type === "success"}
                  error={formMessage.type === "error"}
                  header={formMessage.type === "success" ? "¡Éxito!" : "Error"}
                  content={formMessage.content}
                />
              )}

              <Grid stackable>
                <Grid.Row columns={2}>
                  <Grid.Column>
                    <Form.Field>
                      <label>DNI</label>
                      <Input
                        icon="id card"
                        iconPosition="left"
                        value={userData?.dni || ""}
                        readOnly
                      />
                    </Form.Field>
                  </Grid.Column>
                  <Grid.Column>
                    <Form.Field>
                      <label>Email</label>
                      <Input
                        icon="at"
                        iconPosition="left"
                        name="email"
                        value={formik.values.email}
                        readOnly
                      />
                    </Form.Field>
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row columns={2}>
                  <Grid.Column>
                    <Form.Field>
                      <label>Teléfono principal</label>
                      <Input
                        icon="phone"
                        iconPosition="left"
                        name="phone"
                        placeholder="Ingresa tu teléfono"
                        value={formik.values.phone}
                        readOnly
                      />
                    </Form.Field>
                  </Grid.Column>
                  <Grid.Column>
                    <Form.Field>
                      <label>Teléfono alternativo</label>
                      <Input
                        icon="phone"
                        iconPosition="left"
                        name="phone2"
                        placeholder="Teléfono alternativo"
                        value={formik.values.phone2}
                        onChange={formik.handleChange}
                        disabled={!isEditing}
                      />
                    </Form.Field>
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row columns={2}>
                  <Grid.Column>
                    <Form.Field>
                      <label>Dirección</label>
                      <Input
                        icon="map marker alternate"
                        iconPosition="left"
                        name="address"
                        placeholder="Ingresa tu dirección"
                        value={formik.values.address}
                        onChange={formik.handleChange}
                        disabled={!isEditing}
                      />
                    </Form.Field>
                  </Grid.Column>
                  <Grid.Column>
                    <Form.Field>
                      <label>Fecha de nacimiento</label>
                      <Input
                        icon="calendar"
                        iconPosition="left"
                        value={
                          userData?.birthDate
                            ? new Date(userData.birthDate).toLocaleDateString()
                            : "No especificada"
                        }
                        readOnly
                      />
                    </Form.Field>
                  </Grid.Column>
                </Grid.Row>
              </Grid>

              <div className="personal-info-tab__actions">
                {isEditing ? (
                  <>
                    <Button
                      type="submit"
                      primary
                      disabled={loading}
                      loading={loading}
                    >
                      <Icon name="save" /> Guardar cambios
                    </Button>
                    <Button type="button" onClick={handleCancelEdit}>
                      <Icon name="cancel" /> Cancelar
                    </Button>
                  </>
                ) : (
                  <Button type="button" primary onClick={handleStartEdit}>
                    <Icon name="edit" /> Editar información
                  </Button>
                )}
              </div>
            </Form>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </Segment>
  );
}
